import type {
  AwardCalculation,
  AwardSummary,
  AwardTypeCode,
  AwardView,
  EntryPool,
  LetterPlan,
  OperationalAwardTypeCode,
  PoolCheck,
  PoolPositions,
  WaitingOn,
} from '@myrewards/contracts';
import { checkBudget } from '@myrewards/domain';
import { AWARD_TYPE_LABEL } from '../home/labels';
import {
  formatDate,
  formatMoney,
  formatMonth,
  formatShortDate,
  plural,
  type StatusTone,
} from '../ui';

// Words and numbers for the award screens. Amounts and "Paid from" only: never a split ratio
// (CLAUDE.md), never an S-code, never a raw issue code.

export { AWARD_TYPE_LABEL };

type StageId = AwardView['stages'][number]['id'];

/**
 * The plain stages (CLAUDE.md): the record says "Closed" for a closed award. One name per stage for
 * every award type: APP-252, a Spot award's letter is the Stock Admin team's to check and release
 * again (APP-244), so its stage no longer reads "With the Spot Committee" (APP-110).
 */
export const STAGE_WORDS: Readonly<Record<StageId, { label: string; tone: StatusTone }>> = {
  draft: { label: 'Draft', tone: 'neutral' },
  approval: { label: 'Approval', tone: 'waiting' },
  with_stock_admin: { label: 'With the Stock Admin team', tone: 'info' },
  employee_choice: { label: 'Employee’s choice', tone: 'waiting' },
  paid: { label: 'Paid', tone: 'done' },
  closed: { label: 'Closed', tone: 'done' },
};

/**
 * Which approval an award waits on (QA-014), in the same words as the award register (Insights):
 * "Approval · Executive decision".
 */
export const WAITING_WORDS: Readonly<Record<WaitingOn, { label: string; tone: StatusTone }>> = {
  sector_lead: { label: 'Approval · Sector Lead', tone: 'waiting' },
  named_executive: { label: 'Approval · Executive', tone: 'waiting' },
  executive: { label: 'Approval · Executive decision', tone: 'waiting' },
  executives_outside_app: { label: 'Approval · Executives decide', tone: 'waiting' },
  over_budget: { label: 'Approval · over budget', tone: 'problem' },
  // APP-204: an overage, for someone who may not see the budget: where it is, never why.
  executives: { label: 'Approval · with the Executives', tone: 'waiting' },
};

/** Where an award is, in one status word: its stage (and whose approval), or how it ended. */
export function statusOf(
  award: Pick<AwardSummary, 'stage' | 'ended'> & { waitingOn?: WaitingOn | null | undefined },
): {
  label: string;
  tone: StatusTone;
} {
  if (award.ended === 'cancelled') return { label: 'Cancelled', tone: 'neutral' };
  if (award.ended === 'rejected') return { label: 'Not approved', tone: 'problem' };
  if (award.stage === 'approval' && award.waitingOn != null) return WAITING_WORDS[award.waitingOn];
  return award.stage === null ? STAGE_WORDS.draft : STAGE_WORDS[award.stage];
}

/**
 * A person's recent recognition on the entry form (SL3, SL4 "Recent recognition"): "Last award in
 * May", "No awards in 12 months", and whether this award would make them a repeat recipient. The
 * flag is words, never colour alone.
 */
export function recentWords(
  r: {
    months: number;
    awards: number;
    last: { date: string } | null;
    repeat: boolean;
    wouldBeRepeat: boolean;
  },
  currentYear: number,
): { text: string; flag: string | null } {
  const text =
    r.last === null || r.awards === 0
      ? `No awards in ${String(r.months)} months`
      : `${r.awards > 1 ? `${String(r.awards)} awards in ${String(r.months)} months · last` : 'Last award'} in ${formatMonthYear(r.last.date, currentYear)}`;
  const flag = r.repeat ? 'Repeat' : r.wouldBeRepeat ? 'Would be a repeat' : null;
  return { text, flag };
}

function formatMonthYear(day: string, currentYear: number): string {
  const month = formatMonth(day);
  return Number(day.slice(0, 4)) === currentYear ? month : `${month} ${day.slice(0, 4)}`;
}

/** "Oct 21" this year; "Mar 22, 2025" in any other, so an older row never reads as this year's. */
export function listDate(day: string, currentYear: number): string {
  return Number(day.slice(0, 4)) === currentYear ? formatShortDate(day) : formatDate(day);
}

/** "$2,000 + 100 options", "$2,000", "100 options"; "$0" when there's nothing. */
export function amountText(cashCents: number, options: number): string {
  const parts: string[] = [];
  if (cashCents !== 0 || options === 0) parts.push(formatMoney(cashCents));
  if (options !== 0) parts.push(plural(options, 'option', 'options'));
  return parts.join(' + ');
}

/**
 * A summary's amount: its cash and options. An older PAA draft (APP-151) has only its total value
 * until its cash and options are worked out.
 */
export function summaryAmount(s: AwardSummary): string {
  if (s.type === 'PAA' && s.totalValueCents !== null && s.cashCents === 0 && s.options === 0)
    return `${formatMoney(s.totalValueCents)} value`;
  return amountText(s.cashCents, s.options);
}

/**
 * APP-151: an older PAA, whose amount is its total value split by its saved share. Every PAA since
 * is its cash, with options on top (the server says which, on the award's calculation).
 */
export function paaByValue(view: {
  type: string;
  calculation: Pick<AwardCalculation, 'paaAmount'>;
}): boolean {
  return view.type === 'PAA' && view.calculation.paaAmount === 'total_value';
}

/** "2 people · $3,000 cash · COBRA flight test" for lists. */
/**
 * Who a listed award is for, by name (APP-070 D18): "Maya Ortiz", "Maya Ortiz and Lucia Ferraro",
 * "Maya Ortiz, Lucia Ferraro, Ben Adeyemi and 2 more". Null when the server didn't name them (an
 * older server) or no one is on it yet, so the caller falls back to the count.
 */
export function forWhomWords(s: Pick<AwardSummary, 'people' | 'peopleNames'>): string | null {
  const shown = s.peopleNames ?? [];
  if (shown.length === 0) return null;
  const more = s.people - shown.length;
  if (more > 0) return `${shown.join(', ')} and ${String(more)} more`;
  if (shown.length === 1) return shown[0] ?? null;
  return `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1] ?? ''}`;
}

export function summaryLine(s: AwardSummary): string {
  const parts = [plural(s.people, 'person', 'people'), summaryAmount(s)];
  if (s.title !== null) parts.push(s.title);
  return parts.join(' · ');
}

/** A draft this person may still edit opens as the form; anything else is the record (G1). */
export function isEditableDraft(view: Pick<AwardView, 'stage' | 'ended' | 'actions'>): boolean {
  return view.stage === 'draft' && view.ended === null && view.actions.edit;
}

/** The name an award goes by before it has a number. */
export function awardName(a: Pick<AwardView, 'displayId' | 'title' | 'type'>): string {
  if (a.displayId !== null) return a.title === null ? a.displayId : `${a.displayId} · ${a.title}`;
  return a.title ?? AWARD_TYPE_LABEL[a.type];
}

/** "by Sam Ortega for Graham Whitlock", or "by Graham Whitlock". */
export function enteredBy(a: Pick<AwardView, 'createdBy' | 'onBehalfOf'>): string {
  return a.onBehalfOf === null
    ? `by ${a.createdBy.name}`
    : `by ${a.createdBy.name} for ${a.onBehalfOf.name}`;
}

/** The stages this award uses, in order (skipped ones aren't shown). */
export function usedStages(stages: AwardView['stages']): AwardView['stages'] {
  return stages.filter((s) => s.status !== 'skipped');
}

export interface PaidFromRow {
  pool: { id: string; name: string };
  cashCents: number;
  options: number;
  people: number;
  names: readonly string[];
}

/** "Paid from": each pool with what it pays and who it pays. */
export function paidFromRows(calc: AwardCalculation): PaidFromRow[] {
  return calc.paidFrom.map((p) => {
    const names = calc.lines.filter((l) => l.pool?.id === p.pool.id).map((l) => l.person.name);
    return {
      pool: p.pool,
      cashCents: p.cashCents,
      options: p.options,
      people: names.length,
      names,
    };
  });
}

/**
 * APP-200: "Paid from the PAA/TAA pool. The Executives check its budget when they decide." A TAA
 * pays only some of its people from it.
 */
export function executivesCheckWords(
  type: OperationalAwardTypeCode,
  pools: readonly { name: string }[],
): string {
  const names = pools.map((p) => p.name.replace(/\s+pool$/i, '')).join(' and ');
  const many = pools.length > 1;
  return `${type === 'TAA' ? 'Part is paid' : 'Paid'} from the ${names} ${many ? 'pools' : 'pool'}. The Executives check ${many ? 'their budgets' : 'its budget'} when they decide.`;
}

/** "PAA/TAA pool", "Maritime Airborne pool". */
export function poolName(name: string): string {
  return /\bpool$/i.test(name) ? name : `${name} pool`;
}

export interface BudgetRow {
  pool: { id: string; name: string };
  status: PoolCheck['status'];
  /**
   * `status`: this person doesn't see the pool's figures (APP-111), so `leftNow`, `leftAfter` and
   * `over` are null whatever its status, and the status says where it stands.
   */
  shows: PoolCheck['shows'];
  /** What this award asks of the pool. */
  thisAward: { cashCents: number; options: number };
  /** Before this award: `null` when the budget isn't set, or its figures aren't shown. */
  leftNow: { cashCents: number; options: number } | null;
  /** After this award: negative when it goes over. */
  leftAfter: { cashCents: number; options: number } | null;
  /** How much it goes over, cash and options, when its figures are shown. */
  over: { cashCents: number; options: number } | null;
}

/**
 * A pool from Budget pools as the entry form shows it while its own check loads: with its figures,
 * since Budget pools lists only the pools this person sees (APP-111).
 */
export function startingPoolOf(p: PoolPositions['pools'][number]): EntryPool {
  // The domain's own budget check, with nothing asked of the pool yet (as the server works it out).
  const checked = checkBudget({
    demandByPool: { [p.pool.id]: { cashCents: 0, options: 0 } },
    usageByPool: { [p.pool.id]: { inProgress: p.inProgress, paid: p.paid } },
    budgetByPool: {
      [p.pool.id]: p.budget === null ? { status: 'not_set' } : { status: 'set', ...p.budget },
    },
  });
  const status = checked.ok ? (checked.value[0]?.status ?? 'not_set') : 'not_set';
  return { ...p, shows: 'figures', status };
}

/**
 * A pool already spent before this award (QA-029): its cash left below zero, or, for someone who
 * sees its status alone, over budget (APP-111).
 */
export function spentNow(p: EntryPool): boolean {
  return p.shows === 'figures' ? (p.remaining?.cashCents ?? 0) < 0 : p.status === 'over_budget';
}

/** Whether any pool the award draws on goes over, whether or not its figures are shown. */
export function goesOverBudget(rows: readonly BudgetRow[]): boolean {
  return rows.some((r) => r.status === 'over_budget');
}

/** Each pool the award draws on: left now, this award, left after, or by how much it's over. */
export function budgetRows(calc: AwardCalculation): BudgetRow[] {
  return calc.budget.map((b) => {
    const thisAward = calc.lines
      .filter((l) => l.pool?.id === b.pool.id)
      .reduce(
        (sum, l) => ({ cashCents: sum.cashCents + l.cashCents, options: sum.options + l.options }),
        { cashCents: 0, options: 0 },
      );
    const leftAfter = b.shows === 'figures' ? b.remainingAfter : null;
    return {
      pool: b.pool,
      status: b.status,
      shows: b.shows,
      thisAward,
      leftAfter,
      leftNow:
        leftAfter === null
          ? null
          : {
              cashCents: leftAfter.cashCents + thisAward.cashCents,
              options: leftAfter.options + thisAward.options,
            },
      over:
        b.shows === 'figures' && b.status === 'over_budget' && b.shortfall !== null
          ? { cashCents: b.shortfall.cashCents, options: b.shortfall.options }
          : null,
    };
  });
}

/** By how much the award is over across its pools, in words ("$2,913", "$2,913 and 40 options"). */
export function overByText(rows: readonly BudgetRow[]): string | null {
  const over = rows.filter((r) => r.over !== null);
  if (over.length === 0) return null;
  const cash = over.reduce((s, r) => s + (r.over?.cashCents ?? 0), 0);
  const options = over.reduce((s, r) => s + (r.over?.options ?? 0), 0);
  if (cash > 0 && options > 0)
    return `${formatMoney(cash)} and ${plural(options, 'option', 'options')}`;
  if (options > 0) return plural(options, 'option', 'options');
  return formatMoney(cash);
}

/** The pools an award of this type draws on, by id, as the seed and server name them. */
export function candidatePoolIds(type: AwardTypeCode, sectorId: string | null): string[] {
  if (type === 'EDB') return ['executive'];
  if (type === 'PAA') return ['paa-taa'];
  if (type === 'SDB') return sectorId === null ? [] : [sectorId];
  return sectorId === null ? ['paa-taa'] : ['paa-taa', sectorId];
}

/** The positions of the pools this award draws on, of those this person may see. */
export function positionsFor(
  positions: PoolPositions | null | undefined,
  type: AwardTypeCode,
  sectorId: string | null,
  sectorName: string | null,
): EntryPool[] {
  if (!positions) return [];
  const ids = new Set(candidatePoolIds(type, sectorId));
  return positions.pools
    .filter(
      (p) =>
        ids.has(p.pool.id) ||
        (type !== 'EDB' && type !== 'PAA' && sectorName !== null && p.pool.name === sectorName) ||
        (type === 'EDB' && /executive/i.test(p.pool.name)) ||
        ((type === 'PAA' || type === 'TAA') && /paa/i.test(p.pool.name)),
    )
    .map(startingPoolOf);
}

/**
 * "Letters go to", short, for a fact list (SL7, QA-029): the one holder's name, "Each employee",
 * "Each person's supervisor", or how many hold them when it's chosen person by person.
 */
export function lettersGoTo(plan: LetterPlan): string {
  const held = plan.lines.filter((l) => l.recipient !== null);
  if (plan.lines.length === 0) return 'No one on it yet';
  if (held.length === 0) return 'Chosen when the letters are made';
  if (held.length === plan.lines.length && held.every((l) => l.recipient?.id === l.personId))
    return plan.lines.length === 1 ? 'The employee' : 'Each employee';
  const names = [...new Set(held.map((l) => l.recipient?.name ?? ''))];
  const [only] = names;
  if (names.length === 1 && only !== undefined && held.length === plan.lines.length) return only;
  if (plan.rule === 'manager' && plan.lines.every((l) => l.chosen === null))
    return 'Each person’s supervisor';
  return `${plural(names.length, 'person', 'people')}, as chosen for each`;
}
